import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import Switch from '../../components/Switch'
import { promiseToast } from '../../util/Util'
import Radom from '../../state/Radom'
import LoadingBar from '../../components/LoadingBar'
import { Container } from '../../components/Animations'
import {
  SettingsCard,
  SettingsCardBody,
  SettingsCardDescription,
  SettingsCardHeader,
  SettingsCardTitle,
  SettingsEyebrow,
  SettingsHero,
  SettingsPage,
  SettingsRow,
  SettingsRowDescription,
  SettingsRows,
  SettingsRowTitle,
  SettingsSubtext,
  SettingsTitle,
  StatusPill
} from './SettingsLayout'

const NOTIFICATION_TYPES = [
  {
    name: 'Successful payments',
    id: 'PaymentSuccessful',
    description: 'Receive a notification when a payment completes successfully.'
  },
  {
    name: 'Incomplete payments',
    id: 'PaymentIncomplete',
    description: 'Receive a notification when an incomplete (i.e. partial amount of the total due) payment is made.'
  },
  {
    name: 'New subscriptions',
    id: 'Subscription',
    description: 'Receive a notification when a new subscription is created.'
  },
  {
    name: 'Subscription payment attempt failure',
    id: 'SubscriptionPaymentAttemptFailure',
    description: 'Receive a notification when an auto-pay enabled subscription payment fails.'
  },
  {
    name: 'Withdrawals',
    id: 'Withdrawal',
    description: 'Receive a notification when a withdrawal request completes.'
  }
]

const Notifications = observer(() => {
  const [isLoading, setIsLoading] = useState(true)
  const [notificationSettings, setNotificationSettings] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setIsLoading(true)
    Radom.getNotificationSettings()
      .then(r => setNotificationSettings(r))
      .catch(err => {
        console.error('Failed to load notification settings', err)
        setNotificationSettings({})
      })
      .finally(() => setIsLoading(false))
  }, [])

  const changeSetting = (setting: string): void => {
    const newSettings = { ...notificationSettings, [setting]: !notificationSettings[setting] }
    setNotificationSettings(newSettings)
    promiseToast(Radom.updateNotificationSettings({ settings: newSettings }).catch(err => {
      setNotificationSettings({ ...notificationSettings, [setting]: !!notificationSettings[setting] })
      throw err
    }), 'Saving changes', 'Settings saved', 'Failed to update settings')
  }

  const enabledCount = NOTIFICATION_TYPES.filter(n => notificationSettings[n.id]).length

  return <Container>
    <SettingsPage>
      <SettingsHero>
        <div>
          <SettingsEyebrow>User settings</SettingsEyebrow>
          <SettingsTitle>Notifications</SettingsTitle>
          <SettingsSubtext>
            Choose which operational emails should reach your inbox. These affect account-level alerts only.
          </SettingsSubtext>
        </div>
        <StatusPill $tone={enabledCount > 0 ? 'green' : 'neutral'}>
          {isLoading ? 'Checking' : `${enabledCount} enabled`}
        </StatusPill>
      </SettingsHero>

      <SettingsCard>
        <SettingsCardHeader>
          <div>
            <SettingsCardTitle>Email notifications</SettingsCardTitle>
            <SettingsCardDescription>
              Keep high-signal payment, withdrawal, and subscription activity visible without overwhelming the team.
            </SettingsCardDescription>
          </div>
        </SettingsCardHeader>
        <SettingsCardBody>
          <SettingsRows>
            {NOTIFICATION_TYPES.map(n =>
              <SettingsRow key={n.name}>
                <div>
                  <SettingsRowTitle>{n.name}</SettingsRowTitle>
                  <SettingsRowDescription>{n.description}</SettingsRowDescription>
                </div>
                {isLoading
                  ? <LoadingBar style={{ width: 42, borderRadius: 20 }} />
                  : <Switch
                    sizeScale={0.85}
                    checked={notificationSettings[n.id]}
                    onClick={() => changeSetting(n.id)}
                  />}
              </SettingsRow>
            )}
          </SettingsRows>
        </SettingsCardBody>
      </SettingsCard>
    </SettingsPage>
  </Container>
})

export default Notifications
